import { Component, Input } from '@angular/core';

/** Layout family a homepage section's placeholder falls into. */
type SkLayout = 'hero' | 'cards' | 'steps' | 'strip' | 'split' | 'rows' | 'stats' | 'story';

interface SkSpec {
  layout: SkLayout;
  /** Blocks in the grid/row. */
  count?: number;
  /** Grid columns (cards wrap to `count / cols` rows). */
  cols?: number;
  /** Block height in px. */
  h?: number;
  /** Centre the label + heading bar (most marketing sections do). */
  centred?: boolean;
  /** Trailing call-to-action chip. */
  cta?: boolean;
  /** Media block sits on the left instead of the right (split only). */
  mediaFirst?: boolean;
}

/**
 * Skeleton placeholders for the homepage, one per section (Figma node
 * 14509:272775). Nearly every section in that mock is the same shape — a small
 * label chip, a wide heading bar, then a row or grid of blocks — so the sections
 * are described by the SPECS table below rather than hand-written markup, and
 * only the genuinely different layouts (hero, split, strip) get their own
 * branch. Shimmer and crossfade come from the global `.sk*` classes in
 * the inline <style> in src/index.html; this component is layout only.
 *
 * Keys match the section keys in categories.component.ts, in page order.
 */
@Component({
  selector: 'app-home-skeleton',
  standalone: true,
  templateUrl: './home-skeleton.component.html',
  styleUrl: './home-skeleton.component.css',
})
export class HomeSkeletonComponent {
  @Input() part: string = 'hero';

  private static readonly SPECS: { [k: string]: SkSpec } = {
    hero:         { layout: 'hero' },
    hiw:          { layout: 'steps', count: 4, centred: true, cta: true },
    categories:   { layout: 'cards', count: 6, cols: 3, h: 170, centred: true, cta: true },
    featured:     { layout: 'cards', count: 4, cols: 4, h: 230, centred: true },
    modify:       { layout: 'cards', count: 4, cols: 4, h: 150, centred: true },
    brands:       { layout: 'strip', count: 5, centred: true, cta: true },
    testimonials: { layout: 'cards', count: 3, cols: 3, h: 200, centred: true, cta: true },
    recent:       { layout: 'cards', count: 4, cols: 4, h: 230, centred: true },
    companies:    { layout: 'strip', count: 5, centred: true },
    numbers:      { layout: 'stats', count: 4, centred: true },
    // Our Story is the only left-aligned split on the page (copy left, video right),
    // so it gets its own layout rather than bending 'hero' or 'split' to fit.
    story:        { layout: 'story' },
    insights:     { layout: 'split', h: 220 },
    faq:          { layout: 'rows', count: 5, h: 56, centred: true },
  };

  get spec(): SkSpec {
    return HomeSkeletonComponent.SPECS[this.part] || { layout: 'cards', count: 3, cols: 3, h: 150 };
  }

  /** `count` as a plain array so the template can @for over it. */
  get blocks(): number[] {
    return Array.from({ length: this.spec.count || 0 }, (_, i) => i);
  }

  readonly r2 = [0, 1];
  readonly r3 = [0, 1, 2];
  readonly r4 = [0, 1, 2, 3];
}
